<template>
  <el-dialog
    :visible.sync="visible"
    :title="'Izmena meča'"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    @close="handleClose"
  >
    <div v-if="match" class="match-result-modal">
      <div class="match-info">
        <p class="match-description">
          Podesite takmičare za ovaj meč:
        </p>
      </div>

      <!-- Edit Competitors Section -->
      <div class="edit-competitors-section">
        <div class="competitor-edit-field">
          <label>Takmičar 1:</label>
          <el-select
            v-model="editCompetitorRegId"
            placeholder="Izaberite takmičara ili ostavite prazno za BYE"
            clearable
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="competitor in availableCompetitorsForFirst"
              :key="competitor._id || competitor.id"
              :label="getCompetitorOptionLabel(competitor)"
              :value="competitor._id || competitor.id"
            />
          </el-select>
        </div>
        <div class="competitor-edit-field">
          <label>Takmičar 2:</label>
          <el-select
            v-model="editCompetitorTwoRegId"
            placeholder="Izaberite takmičara ili ostavite prazno za BYE"
            clearable
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="competitor in availableCompetitorsForSecond"
              :key="competitor._id || competitor.id"
              :label="getCompetitorOptionLabel(competitor)"
              :value="competitor._id || competitor.id"
            />
          </el-select>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose" :disabled="submitting">Otkaži</el-button>
      <el-button
        type="primary"
        :disabled="submitting"
        :loading="submitting"
        @click="handleUpdateCompetitors"
      >
        Sačuvaj izmene
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'MatchResultModal',

  props: {
    match: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    bracket: {
      type: Object,
      default: null
    },
    bracketCompetitors: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      submitting: false,
      editCompetitorRegId: null,
      editCompetitorTwoRegId: null
    }
  },

  computed: {
    availableCompetitors () {
      // Return only competitors that are actually in this bracket
      // No additional filtering needed since bracketCompetitors already contains
      // only the competitors from the first round matches of this specific bracket
      return this.bracketCompetitors || []
    },

    availableCompetitorsForFirst () {
      // Exclude the competitor selected in the second dropdown
      if (!this.editCompetitorTwoRegId) {
        return this.availableCompetitors
      }
      return this.availableCompetitors.filter(competitor => {
        const competitorId = competitor._id || competitor.id
        return String(competitorId) !== String(this.editCompetitorTwoRegId)
      })
    },

    availableCompetitorsForSecond () {
      // Exclude the competitor selected in the first dropdown
      if (!this.editCompetitorRegId) {
        return this.availableCompetitors
      }
      return this.availableCompetitors.filter(competitor => {
        const competitorId = competitor._id || competitor.id
        return String(competitorId) !== String(this.editCompetitorRegId)
      })
    }
  },

  watch: {
    visible (newVal) {
      if (newVal && this.match) {
        this.editCompetitorRegId = this.match.competitorRegId || null
        this.editCompetitorTwoRegId = this.match.competitorTwoRegId || null
      }
    },
    match () {
      if (this.match) {
        this.editCompetitorRegId = this.match.competitorRegId || null
        this.editCompetitorTwoRegId = this.match.competitorTwoRegId || null
      }
    }
  },

  methods: {
    getCompetitorOptionLabel (competitor) {
      if (!competitor) return 'Nepoznato'

      // For team registrations
      if (competitor.isTeam && competitor.teamMembers && competitor.teamMembers.length > 0) {
        const teamMembers = competitor.teamMembers.map(member => {
          const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
          const gender = member.isMale ? 'M' : 'Ž'
          return `${name} (${gender})`
        }).join(', ')

        if (competitor.club && competitor.club.shortName) {
          return `${teamMembers} - ${competitor.club.shortName}`
        }

        return teamMembers
      }

      // For individual registrations
      if (!competitor.competitor) return 'Nepoznato'

      const competitorData = competitor.competitor
      const name = `${competitorData.firstName || ''} ${competitorData.lastName || ''}`.trim()
      const club = competitor.club ? (competitor.club.shortName || competitor.club.name) : ''

      if (club) {
        return `${name} - ${club}`
      }

      return name || 'Nepoznato'
    },

    handleClose () {
      if (this.submitting) return
      this.$emit('update:visible', false)
    },

    async handleUpdateCompetitors () {
      if (!this.match) return

      const competitorRegId = this.editCompetitorRegId ? parseInt(this.editCompetitorRegId) : null
      const competitorTwoRegId = this.editCompetitorTwoRegId ? parseInt(this.editCompetitorTwoRegId) : null

      // Validate that the same competitor is not selected twice
      if (competitorRegId && competitorTwoRegId && String(competitorRegId) === String(competitorTwoRegId)) {
        this.$message.error('Isti takmičar ne može biti izabran na obe pozicije')
        return
      }

      try {
        await this.$confirm(
          'Da li ste sigurni da želite da ažurirate takmičare za ovaj meč?',
          'Potvrda izmene',
          {
            confirmButtonText: 'Potvrdi',
            cancelButtonText: 'Otkaži',
            type: 'warning'
          }
        )

        this.submitting = true

        await this.$store.dispatch('tournaments/updateMatchCompetitors', {
          matchId: this.match.id,
          competitorRegId: competitorRegId,
          competitorTwoRegId: competitorTwoRegId
        })

        this.$message.success('Takmičari su uspešno ažurirani')
        this.$emit('result-submitted')
        this.handleClose()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error updating match competitors:', error)
          const errorMessage =
            error.response?.data?.error ||
            'Greška pri ažuriranju takmičara'
          this.$message.error(errorMessage)
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.match-result-modal {
  padding: 10px 0;
}

.match-info {
  margin-bottom: 20px;

  .match-description {
    margin: 0;
    font-size: 14px;
    color: #666;
  }
}

.edit-competitors-section {
  margin-top: 0;
}

.competitor-edit-field {
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
  }

  .current-competitor-info {
    margin-top: 8px;
    font-size: 12px;
    color: #666;
    font-style: italic;
  }
}
</style>
